/**
 * SlotRenderer — generic factory for shadcn compound components.
 *
 * Maps Cogo JSON type → shadcn React component 1:1.
 * No internal structure transformation. className pass-through. Children recursive.
 *
 * Usage:
 *   registry.register("card-header", createSlotRenderer(CardHeader))
 *   registry.register("empty-title", createSlotTextRenderer(EmptyTitle))
 */
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"

/**
 * LLM-authored props that flow from Cogo JSON element → shadcn React component.
 * These mirror shadcn/HTML prop names so LLM-generated JSON matches shadcn examples.
 */
const PASS_THROUGH_PROPS = [
  "value", "defaultValue",
  "checked", "defaultChecked",
  "disabled", "readonly", "readOnly", "required",
  "variant", "size",
  "align", "orientation",
  "colSpan", "htmlFor",
  "min", "max", "step",
  "name",
  "placeholder",
  "open", "defaultOpen",
  "asChild",
  // Phase 5703 (v64.2): semantic heading tag for CardTitle / EmptyTitle / etc.
  // Maps to React `as` prop on shadcn title components that support polymorphism.
  "tag",
] as const

function extractProps(element: Record<string, unknown>): Record<string, unknown> {
  const props: Record<string, unknown> = {}
  for (const key of PASS_THROUGH_PROPS) {
    if (element[key] !== undefined) {
      // Normalize lowercase readonly → React readOnly
      const reactKey = key === "readonly" ? "readOnly" : key
      props[reactKey] = element[key]
    }
  }
  if (element.for !== undefined && props.htmlFor === undefined) {
    props.htmlFor = element.for
  }
  return props
}

function eventPropsFor(
  element: RendererProps["element"],
  dispatchEvent: ReturnType<typeof useRenderContext>["dispatchEvent"],
  itemContext: ReturnType<typeof useRenderContext>["itemContext"],
): Record<string, unknown> {
  const events = element.events && typeof element.events === "object"
    ? element.events as Record<string, unknown>
    : {}
  const payloadFor = (binding: unknown) => {
    const params = binding && typeof binding === "object"
      ? (binding as Record<string, unknown>).params
      : undefined
    return params && typeof params === "object"
      ? { ...(params as Record<string, unknown>), ...(itemContext ?? {}) }
      : { ...(itemContext ?? {}) }
  }
  const result: Record<string, unknown> = {}
  const clickBinding = events.click ?? events.tap
  if (clickBinding !== undefined) {
    result.onClick = () => dispatchEvent(
      element.id,
      events.click !== undefined ? "click" : "tap",
      payloadFor(clickBinding),
    )
  }
  if (events.contextmenu !== undefined || events.contextMenu !== undefined) {
    const binding = events.contextmenu ?? events.contextMenu
    result.onContextMenu = () => dispatchEvent(element.id, "contextmenu", payloadFor(binding))
  }
  return result
}

/**
 * Container slot: renders Component with className + recursive children.
 * For structural components like CardHeader, CardContent, EmptyHeader, ItemContent, etc.
 *
 * NOTE: `Component` is typed as `React.ElementType` (not a strict `ComponentType<{...}>`)
 * so that strict base-ui wrappers (e.g. TabsTrigger with `TabsPrimitive.Tab.Props`,
 * RadioGroupItem with `RadioPrimitive.Root.Props`) can be passed through unchanged.
 * The shape contract (accepts className + children + pass-through props) is enforced
 * by convention at registration sites in `renderers/index.ts`.
 * (Plan 002-03 SIGNATURE fix for TS2345 sites 7-9.)
 */
export function createSlotRenderer(
  Component: React.ElementType,
  defaultProps?: Record<string, unknown>,
) {
  return function SlotRenderer({ element }: RendererProps) {
    const { dispatchEvent, itemContext } = useRenderContext()
    const lifted = liftV4Props(element)
    const passProps = extractProps(lifted as unknown as Record<string, unknown>)
    const eventProps = eventPropsFor(lifted, dispatchEvent, itemContext)
    return (
      <Component
        className={cn(lifted.className)}
        data-cogo-element-id={lifted.id}
        data-cogo-type={lifted.type}
        {...defaultProps}
        {...passProps}
        {...eventProps}
      >
        {lifted.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Component>
    )
  }
}

/**
 * Text slot: renders Component with className + resolved text content.
 * For text components like CardTitle, EmptyTitle, ItemTitle, AlertTitle, etc.
 *
 * See `createSlotRenderer` note above — same `React.ElementType` rationale.
 */
export function createSlotTextRenderer(
  Component: React.ElementType,
) {
  return function SlotTextRenderer({ element }: RendererProps) {
    const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
    const lifted = liftV4Props(element)
    const text = lifted.text ? String(resolveSymbol(lifted.text) ?? "") : ""
    const passProps = extractProps(lifted as unknown as Record<string, unknown>)
    const eventProps = eventPropsFor(lifted, dispatchEvent, itemContext)
    const iconName = lifted.icon ? String(resolveSymbol(lifted.icon) ?? "") : ""
    const Icon = iconName ? resolveIcon(iconName) : undefined
    const icon = Icon ? <Icon className={lifted.iconClassName ?? "size-4"} aria-hidden="true" /> : null
    const iconRight = lifted.iconPosition === "right"

    return (
      <Component
        className={cn(lifted.className)}
        data-cogo-element-id={lifted.id}
        data-cogo-type={lifted.type}
        {...passProps}
        {...eventProps}
      >
        {!iconRight ? icon : null}
        {text}
        {iconRight ? icon : null}
        {lifted.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Component>
    )
  }
}
